import * as React from "react";
import { CheckCircle2, ChevronDown, Loader2, XCircle } from "lucide-react";
import { cn } from "@finster/ui";

import { SimpleMarkdown } from "../SimpleMarkdown";
import { SkillActiveActivity } from "../../../shared/skills/components/SkillActiveActivity";
import type {
  AgentTimelineState,
  HopView,
  SubAgentView,
  ToolView,
} from "../../services/ai/agentStream";
import {
  getActiveSkillReferences,
  parseNarratedReasoningSections,
  visibleAgentHops,
} from "../../services/ai/agentStream";
import {
  getAgentToolColorClass,
  getAgentToolDisplayName,
  isResearchTool,
  isSlideTool,
} from "./agentTimelineMeta";

type CitationClickHandler = (id: string, event: React.MouseEvent<HTMLElement>) => void;

const TIMELINE_MARKDOWN_CLASS = cn(
  "[&_h1]:!text-[12.5px]",
  "[&_h2]:!text-[12px]",
  "[&_h3]:!text-[11.5px]",
  "[&_h4]:!text-[11px]",
  "[&_div]:!text-[11.5px]",
  "[&_li]:!text-[11.5px]",
  "[&_ol]:!text-[11.5px]",
  "[&_ul]:!text-[11.5px]"
);

const StreamingCursor: React.FC = () => (
  <span className="ml-0.5 inline-block h-[1.1em] w-[2px] animate-pulse bg-primary/60 align-text-bottom" />
);

const NarratedThinking: React.FC<{
  text: string;
  onCitationClick?: CitationClickHandler;
}> = ({ text, onCitationClick }) => {
  const sections = parseNarratedReasoningSections(text);
  const titledSections = sections.filter((section) => section.title.length > 0);

  if (titledSections.length === 0) {
    return (
      <SimpleMarkdown
        text={text}
        compact
        onCitationClick={onCitationClick}
        className={TIMELINE_MARKDOWN_CLASS}
      />
    );
  }

  return (
    <div className="flex flex-col gap-2.5">
      {sections.map((section, index) => (
        <div key={`${section.title || "preamble"}-${index}`} className="min-w-0">
          {section.title && (
            <div className="text-[12.5px] font-semibold leading-snug text-foreground">
              {section.title}
            </div>
          )}
          {section.content && (
            <SimpleMarkdown
              text={section.content}
              compact
              onCitationClick={onCitationClick}
              className={cn(TIMELINE_MARKDOWN_CLASS, section.title && "mt-1")}
            />
          )}
        </div>
      ))}
    </div>
  );
};

export const StreamingDots: React.FC = () => (
  <span className="inline-flex items-center gap-[3px]">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="size-1 animate-pulse rounded-full bg-primary/60"
        style={{ animationDelay: `${i * 200}ms` }}
      />
    ))}
  </span>
);

function truncate(text: string, max = 60): string {
  const clean = text.trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1).trimEnd()}…`;
}

const ToolStatusIcon: React.FC<{ status: ToolView["status"]; tool: string }> = ({
  status,
  tool,
}) => {
  if (status === "failed") return <XCircle className="size-4 shrink-0" />;
  if (status === "calling") return <Loader2 className="size-4 shrink-0 animate-spin" />;
  return <CheckCircle2 className={cn("size-4 shrink-0", getAgentToolColorClass(tool))} />;
};

const ToolPill: React.FC<{
  tool: string;
  status: ToolView["status"];
  entity?: string;
  label?: string;
}> = ({ tool, status, entity, label: labelOverride }) => {
  const label = labelOverride ?? getAgentToolDisplayName(tool);
  const contextLabel = entity ? truncate(entity) : "";
  // Slide-mutation tools are internal mechanics — never surface a red failure to
  // the user (the agent self-corrects within the loop). Display-only coercion.
  const effectiveStatus =
    status === "failed" && isSlideTool(tool) && tool !== "apply_preslink" ? "completed" : status;
  return (
    <span
      title={entity ? `${label} (${entity})` : label}
      className={cn(
        "inline-flex h-7 max-w-full cursor-default items-center gap-1.5",
        "rounded-full border bg-background px-3 text-[13px] leading-none",
        effectiveStatus === "failed" ? "border-destructive/40 text-destructive" : "border-border",
        effectiveStatus === "calling" && "text-muted-foreground opacity-70"
      )}
    >
      <ToolStatusIcon status={effectiveStatus} tool={tool} />
      <span className="shrink-0 font-medium">{label}</span>
      {contextLabel && (
        <>
          <span className="text-muted-foreground/70">·</span>
          <span className="max-w-[28ch] truncate text-xs font-normal text-muted-foreground">
            {contextLabel}
          </span>
        </>
      )}
    </span>
  );
};

interface FinsterCitation {
  metadata?: { dataSource?: string; ticker?: string };
  client?: string;
}

const CitationChips: React.FC<{ citations: unknown[] }> = ({ citations }) => {
  if (!citations.length) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {citations.slice(0, 8).map((raw, index) => {
        const citation = (raw ?? {}) as FinsterCitation;
        // `citation.client` is the raw finster sub-agent tool name (e.g.
        // "get_sec_data"); map it to a friendly label when finster doesn't
        // provide a `metadata.dataSource`.
        const source =
          citation.metadata?.dataSource ||
          (citation.client ? getAgentToolDisplayName(citation.client) : "") ||
          "Source";
        const ticker = citation.metadata?.ticker;
        return (
          <span
            key={index}
            className="inline-flex items-center gap-1 rounded-md border border-border/60 bg-muted/30 px-2 py-0.5 text-[10.5px] text-muted-foreground"
          >
            <span className="font-medium text-foreground/70">{source}</span>
            {ticker && <span className="text-muted-foreground/70">{ticker}</span>}
          </span>
        );
      })}
    </div>
  );
};

const NestedSubAgentTimeline: React.FC<{ sub: SubAgentView }> = ({ sub }) => {
  return (
    <div className="relative mt-2 ml-4 border-l border-border/60 pl-3">
      <div className="flex flex-col gap-3">
        {sub.hops.map((subHop) => (
          <div key={subHop.subHop} className="min-w-0">
            {subHop.tools.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {subHop.tools.map((subTool) => (
                  <ToolPill
                    key={subTool.subCallId}
                    tool={subTool.tool}
                    status={subTool.status}
                    entity={subTool.description}
                  />
                ))}
              </div>
            )}
            {subHop.thinkingText.trim() && (
              <div className="mt-1.5 text-[11.5px] leading-relaxed text-muted-foreground/90">
                <SimpleMarkdown text={subHop.thinkingText} compact />
              </div>
            )}
          </div>
        ))}
        {!sub.finalAnswer && sub.liveAnswer.trim() && (
          <div className="text-[11.5px] leading-relaxed text-muted-foreground">
            <SimpleMarkdown text={sub.liveAnswer} compact />
            <StreamingCursor />
          </div>
        )}
      </div>
    </div>
  );
};

const ToolResultCard: React.FC<{
  answer: string;
  citations: unknown[];
  onCitationClick?: CitationClickHandler;
}> = ({ answer, citations, onCitationClick }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <section className="mt-2 overflow-hidden rounded-lg border border-border/60 bg-background">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className={cn(
          "flex w-full items-center gap-2 px-3 py-2 text-left",
          "border-0 bg-transparent transition-colors hover:bg-muted/25"
        )}
      >
        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-[12px] font-semibold text-primary">
          Result
        </span>
        <ChevronDown
          className={cn(
            "h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform",
            open && "rotate-180"
          )}
          aria-hidden
        />
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 text-foreground/80">
          <SimpleMarkdown text={answer} compact onCitationClick={onCitationClick} />
          <CitationChips citations={citations} />
        </div>
      )}
    </section>
  );
};

const ToolRow: React.FC<{
  tool: ToolView;
  onCitationClick?: CitationClickHandler;
  flushLeft?: boolean;
}> = ({ tool, onCitationClick, flushLeft = false }) => {
  const research = isResearchTool(tool.tool);
  const answer = (tool.result?.data as { answer?: string } | undefined)?.answer;
  const citations = (tool.result?.data as { citations?: unknown[] } | undefined)?.citations ?? [];
  const modelSummary = (tool.result?.data as { modelSummary?: string } | undefined)?.modelSummary;

  if (tool.tool === "apply_preslink") {
    const isCalling = tool.status === "calling";
    const progressMessages = (tool.progress ?? [])
      .map((message) => message.replace(/^PresLink:\s*/i, "").trim())
      .filter(Boolean);
    const processPresentation = (tool.result?.data as { processPresentation?: unknown } | undefined)
      ?.processPresentation;
    const isUpdate = Boolean(processPresentation) || progressMessages.length > 0;
    return (
      <div className="min-w-0">
        <ToolPill
          tool={tool.tool}
          status={tool.status}
          label={
            isCalling
              ? isUpdate
                ? "Updating PresLink data"
                : "Resolving PresLink data"
              : tool.status === "failed"
                ? isUpdate
                  ? "PresLink update failed"
                  : "PresLink lookup failed"
                : isUpdate
                  ? "PresLink update complete"
                  : "PresLink lookup complete"
          }
        />
        {progressMessages.length > 0 && (
          <div
            role="log"
            aria-live="polite"
            aria-relevant="additions"
            aria-label="PresLink progress"
            className="mt-2 flex flex-col gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2 text-[11.5px] leading-relaxed text-foreground/80"
          >
            {progressMessages.map((message, index) => {
              const isLatest = index === progressMessages.length - 1;
              const showSpinner = isCalling && isLatest;
              const showError = tool.status === "failed" && isLatest;
              return (
                <div key={`${index}-${message}`} className="flex items-start gap-2">
                  {showSpinner ? (
                    <Loader2 className="mt-0.5 size-3.5 shrink-0 animate-spin text-primary" />
                  ) : showError ? (
                    <XCircle className="mt-0.5 size-3.5 shrink-0 text-destructive" />
                  ) : (
                    <CheckCircle2 className="mt-0.5 size-3.5 shrink-0 text-primary" />
                  )}
                  <span>{message}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  if (tool.tool === "apply_edits") {
    const isCalling = tool.status === "calling";
    return (
      <div className={cn("min-w-0", flushLeft && "-ml-4")}>
        <ToolPill
          tool={tool.tool}
          status={tool.status}
          label={isCalling ? "Applying slide edits" : "Slide edits applied"}
        />
        {tool.status !== "calling" && modelSummary && (
          <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{modelSummary}</p>
        )}
      </div>
    );
  }

  return (
    <div className="min-w-0">
      <ToolPill
        tool={tool.tool}
        status={tool.status}
        entity={isSlideTool(tool.tool) ? undefined : tool.argsPreview}
      />
      {research && tool.sub && tool.status === "calling" && (
        <NestedSubAgentTimeline sub={tool.sub} />
      )}
      {research && tool.status === "completed" && answer && (
        <ToolResultCard answer={answer} citations={citations} onCitationClick={onCitationClick} />
      )}
    </div>
  );
};

const HopRow: React.FC<{
  hop: HopView;
  isActive: boolean;
  onCitationClick?: CitationClickHandler;
}> = ({ hop, isActive, onCitationClick }) => {
  const hasThinking = hop.thinkingText.trim().length > 0;
  const isApplyOnlyToolRow =
    !hasThinking && hop.tools.length === 1 && hop.tools[0]?.tool === "apply_edits";
  return (
    <div className={cn("relative", isApplyOnlyToolRow && "-ml-4")}>
      {!isApplyOnlyToolRow && (
        <span
          className={cn(
            "absolute -left-4 block size-[7px] rounded-full ring-2 ring-background",
            hasThinking ? "top-[6px]" : "top-2.5",
            isActive ? "animate-pulse bg-primary" : "bg-primary/60"
          )}
          aria-hidden
        />
      )}
      <div className="min-w-0">
        {hasThinking && (
          <div className="text-[11.5px] leading-relaxed text-muted-foreground">
            <NarratedThinking text={hop.thinkingText} onCitationClick={onCitationClick} />
          </div>
        )}
        {hop.tools.length > 0 && (
          <div className={cn("flex flex-col gap-2", hasThinking && "mt-2")}>
            {hop.tools.map((tool) => (
              <ToolRow
                key={tool.callId}
                tool={tool}
                onCitationClick={onCitationClick}
                flushLeft={hasThinking && tool.tool === "apply_edits"}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Renders the hop timeline (thinking + tool pills + inline finster/factset
 * result cards + nested sub-agent timeline). The collapsible header is owned by
 * the integrator (chat `StreamingThinking` / deck "Agent activity"). The
 * terminal `timeline.output` is NOT rendered here — the chat bubble extracts the
 * `reply` from it.
 */
export const AgentTimeline: React.FC<{
  timeline: AgentTimelineState;
  streaming: boolean;
  onCitationClick?: CitationClickHandler;
  reducedMotion?: boolean;
}> = ({ timeline, streaming, onCitationClick }) => {
  const activeSkills = getActiveSkillReferences(timeline);
  const visibleHops = visibleAgentHops(timeline);
  if (activeSkills.length === 0 && visibleHops.length === 0) return null;
  const lastIndex = visibleHops.length - 1;
  return (
    <div className="relative pl-4">
      <div className="flex flex-col gap-4">
        {activeSkills.length > 0 && (
          <div aria-label="Active Skills" className="-ml-4 flex flex-col items-start gap-2">
            {activeSkills.map((skill) => (
              <SkillActiveActivity key={skill.id} skill={skill} />
            ))}
          </div>
        )}
        {visibleHops.map((hop, index) => (
          <HopRow
            key={hop.hop}
            hop={hop}
            isActive={streaming && index === lastIndex}
            onCitationClick={onCitationClick}
          />
        ))}
      </div>
    </div>
  );
};

export default AgentTimeline;
